import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Import API utilities
import api, { getCurrentMode } from "../../../utils/api";

// Import components
import SearchHeader from "./components/SearchHeader";
import AdvancedFilterPanel from "./components/AdvancedFilterPanel";
import FilterChips from "./components/FilterChips";
import SearchResultsGrid from "./components/SearchResultsGrid";
import SearchPagination from "./components/SearchPagination";
import SavedSearchesPanel from "./components/SavedSearchesPanel";

import SortControls from "./components/SortControls";
import NoResultsFound from "./components/NoResultsFound";

// Mock data for athletes
import { generateMockAthletes } from "./mockData";

const AthleteSearchResults = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query params from URL
  const queryParams = new URLSearchParams(location.search);

  // State for search query
  const [searchQuery, setSearchQuery] = useState(queryParams.get("q") || "");

  // State for view mode (grid or list)
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("athleteSearchViewMode") || "grid"
  );

  // State for filters
  const [filters, setFilters] = useState({
    physical: {
      heightMin: queryParams.get("heightMin")
        ? parseInt(queryParams.get("heightMin"))
        : 60, // 5'0"
      heightMax: queryParams.get("heightMax")
        ? parseInt(queryParams.get("heightMax"))
        : 84, // 7'0"
      weightMin: queryParams.get("weightMin")
        ? parseInt(queryParams.get("weightMin"))
        : 100,
      weightMax: queryParams.get("weightMax")
        ? parseInt(queryParams.get("weightMax"))
        : 350,
      verticalJumpMin: queryParams.get("verticalJumpMin")
        ? parseInt(queryParams.get("verticalJumpMin"))
        : 0,
      verticalJumpMax: queryParams.get("verticalJumpMax")
        ? parseInt(queryParams.get("verticalJumpMax"))
        : 50,
    },
    academic: {
      gpaMin: queryParams.get("gpaMin")
        ? parseFloat(queryParams.get("gpaMin"))
        : 2.0,
      gpaMax: queryParams.get("gpaMax")
        ? parseFloat(queryParams.get("gpaMax"))
        : 4.0,
      satMin: queryParams.get("satMin")
        ? parseInt(queryParams.get("satMin"))
        : 400,
      satMax: queryParams.get("satMax")
        ? parseInt(queryParams.get("satMax"))
        : 1600,
    },
    track: {
      fortyMin: queryParams.get("fortyMin")
        ? parseFloat(queryParams.get("fortyMin"))
        : 4.0,
      fortyMax: queryParams.get("fortyMax")
        ? parseFloat(queryParams.get("fortyMax"))
        : 6.0,
      hundredMin: queryParams.get("hundredMin")
        ? parseFloat(queryParams.get("hundredMin"))
        : 9.5,
      hundredMax: queryParams.get("hundredMax")
        ? parseFloat(queryParams.get("hundredMax"))
        : 14.0,
      twoHundredMin: queryParams.get("twoHundredMin")
        ? parseFloat(queryParams.get("twoHundredMin"))
        : 19.0,
      twoHundredMax: queryParams.get("twoHundredMax")
        ? parseFloat(queryParams.get("twoHundredMax"))
        : 28.0,
      fourHundredMin: queryParams.get("fourHundredMin")
        ? parseFloat(queryParams.get("fourHundredMin"))
        : 43.0,
      fourHundredMax: queryParams.get("fourHundredMax")
        ? parseFloat(queryParams.get("fourHundredMax"))
        : 70.0,
    },
    strength: {
      benchPressMin: queryParams.get("benchPressMin")
        ? parseInt(queryParams.get("benchPressMin"))
        : 0,
      benchPressMax: queryParams.get("benchPressMax")
        ? parseInt(queryParams.get("benchPressMax"))
        : 500,
      benchRepsMin: queryParams.get("benchRepsMin")
        ? parseInt(queryParams.get("benchRepsMin"))
        : 0,
      benchRepsMax: queryParams.get("benchRepsMax")
        ? parseInt(queryParams.get("benchRepsMax"))
        : 50,
      squatMin: queryParams.get("squatMin")
        ? parseInt(queryParams.get("squatMin"))
        : 0,
      squatMax: queryParams.get("squatMax")
        ? parseInt(queryParams.get("squatMax"))
        : 700,
    },
    sportSpecific: {
      sport: queryParams.get("sport") || "football",
      position: queryParams.get("position") || "",
      recruitingStatus: queryParams.get("recruitingStatus") || "",
      stats: {},
    },
    location: {
      state: queryParams.get("state") || "",
      city: queryParams.get("city") || "",
      radius: queryParams.get("radius")
        ? parseInt(queryParams.get("radius"))
        : 50,
    },
    graduationYear: queryParams.get("graduationYear")
      ? parseInt(queryParams.get("graduationYear"))
      : null,
  });

  // State for active filters
  const [activeFilters, setActiveFilters] = useState([]);

  // State for search results
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  // State for pagination
  const [page, setPage] = useState(
    queryParams.get("page") ? parseInt(queryParams.get("page")) : 1
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    queryParams.get("limit") ? parseInt(queryParams.get("limit")) : 20
  );

  // State for sorting
  const [sortBy, setSortBy] = useState(queryParams.get("sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(
    queryParams.get("sortOrder") || "asc"
  );

  // State for filter panel (closed by default on mobile)
  const [filterPanelOpen, setFilterPanelOpen] = useState(!isMobile);

  // State for saved searches panel
  const [savedSearchesPanelOpen, setSavedSearchesPanelOpen] = useState(false);

  // Debounce function for search
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Update URL with current search params
  const updateURL = useCallback(() => {
    const params = new URLSearchParams();

    // Add search query
    if (searchQuery) params.set("q", searchQuery);

    // Add pagination
    params.set("page", page.toString());
    params.set("limit", rowsPerPage.toString());

    // Add sorting
    params.set("sortBy", sortBy);
    params.set("sortOrder", sortOrder);

    // Add filters
    // Physical
    if (filters.physical.heightMin !== 60)
      params.set("heightMin", filters.physical.heightMin.toString());
    if (filters.physical.heightMax !== 84)
      params.set("heightMax", filters.physical.heightMax.toString());
    if (filters.physical.weightMin !== 100)
      params.set("weightMin", filters.physical.weightMin.toString());
    if (filters.physical.weightMax !== 350)
      params.set("weightMax", filters.physical.weightMax.toString());
    if (filters.physical.verticalJumpMin !== 0)
      params.set(
        "verticalJumpMin",
        filters.physical.verticalJumpMin.toString()
      );
    if (filters.physical.verticalJumpMax !== 50)
      params.set(
        "verticalJumpMax",
        filters.physical.verticalJumpMax.toString()
      );

    // Academic
    if (filters.academic.gpaMin !== 2.0)
      params.set("gpaMin", filters.academic.gpaMin.toString());
    if (filters.academic.gpaMax !== 4.0)
      params.set("gpaMax", filters.academic.gpaMax.toString());
    if (filters.academic.satMin !== 400)
      params.set("satMin", filters.academic.satMin.toString());
    if (filters.academic.satMax !== 1600)
      params.set("satMax", filters.academic.satMax.toString());

    // Track
    if (filters.track.fortyMin !== 4.0)
      params.set("fortyMin", filters.track.fortyMin.toString());
    if (filters.track.fortyMax !== 6.0)
      params.set("fortyMax", filters.track.fortyMax.toString());
    if (filters.track.hundredMin !== 9.5)
      params.set("hundredMin", filters.track.hundredMin.toString());
    if (filters.track.hundredMax !== 14.0)
      params.set("hundredMax", filters.track.hundredMax.toString());
    if (filters.track.twoHundredMin !== 19.0)
      params.set("twoHundredMin", filters.track.twoHundredMin.toString());
    if (filters.track.twoHundredMax !== 28.0)
      params.set("twoHundredMax", filters.track.twoHundredMax.toString());
    if (filters.track.fourHundredMin !== 43.0)
      params.set("fourHundredMin", filters.track.fourHundredMin.toString());
    if (filters.track.fourHundredMax !== 70.0)
      params.set("fourHundredMax", filters.track.fourHundredMax.toString());

    // Strength
    if (filters.strength.benchPressMin !== 0)
      params.set("benchPressMin", filters.strength.benchPressMin.toString());
    if (filters.strength.benchPressMax !== 500)
      params.set("benchPressMax", filters.strength.benchPressMax.toString());
    if (filters.strength.benchRepsMin !== 0)
      params.set("benchRepsMin", filters.strength.benchRepsMin.toString());
    if (filters.strength.benchRepsMax !== 50)
      params.set("benchRepsMax", filters.strength.benchRepsMax.toString());
    if (filters.strength.squatMin !== 0)
      params.set("squatMin", filters.strength.squatMin.toString());
    if (filters.strength.squatMax !== 700)
      params.set("squatMax", filters.strength.squatMax.toString());

    // Sport Specific
    if (filters.sportSpecific.sport !== "football")
      params.set("sport", filters.sportSpecific.sport);
    if (filters.sportSpecific.position)
      params.set("position", filters.sportSpecific.position);
    if (filters.sportSpecific.recruitingStatus)
      params.set("recruitingStatus", filters.sportSpecific.recruitingStatus);

    // Location
    if (filters.location.state) params.set("state", filters.location.state);
    if (filters.location.city) params.set("city", filters.location.city);
    if (filters.location.radius !== 50)
      params.set("radius", filters.location.radius.toString());

    // Graduation Year
    if (filters.graduationYear)
      params.set("graduationYear", filters.graduationYear.toString());

    // Update URL without reloading the page
    navigate({ search: params.toString() }, { replace: true });
  }, [searchQuery, page, rowsPerPage, sortBy, sortOrder, filters, navigate]);

  // Fetch search results
  const fetchResults = useCallback(async () => {
    // Don't auto-search if there are no filters or search query on initial load
    const hasFilters = Object.values(filters).some((category) => {
      if (typeof category === "object") {
        return Object.values(category).some((value) => {
          if (typeof value === "string") return value !== "";
          if (typeof value === "number") {
            // Check if any numeric filter is different from default
            if (category === filters.physical) {
              return (
                value !== 60 &&
                value !== 84 &&
                value !== 100 &&
                value !== 350 &&
                value !== 0 &&
                value !== 50
              );
            }
            if (category === filters.academic) {
              return (
                value !== 2.0 &&
                value !== 4.0 &&
                value !== 400 &&
                value !== 1600
              );
            }
            if (category === filters.track) {
              return (
                value !== 4.0 &&
                value !== 6.0 &&
                value !== 9.5 &&
                value !== 14.0 &&
                value !== 19.0 &&
                value !== 28.0 &&
                value !== 43.0 &&
                value !== 70.0
              );
            }
            if (category === filters.strength) {
              return (
                value !== 0 && value !== 500 && value !== 50 && value !== 700
              );
            }
            if (category === filters.location) {
              return value !== 50;
            }
          }
          return false;
        });
      }
      return false;
    });

    const hasQuery = searchQuery.trim().length > 0;

    // Only auto-search if we have URL parameters, filters, or search query
    if (!hasQuery && !hasFilters && Object.keys(queryParams).length === 0) {
      setIsLoading(false);
      setResults([]);
      setTotalResults(0);
      return;
    }

    setIsLoading(true);

    try {
      console.log("AthleteSearchResults: Current mode is:", getCurrentMode()); // Debug log

      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - use mock data
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const mockData = generateMockAthletes(100);
        setResults(mockData);
        setTotalResults(mockData.length);
      } else {
        // Development mode - fetch from MongoDB
        console.log("AthleteSearchResults: Fetching prospects from API..."); // Debug log

        const prospects = await api.prospects.getAll();
        console.log(
          "AthleteSearchResults: API response - prospects:",
          prospects
        ); // Debug log

        // Transform prospects data to match the expected athlete search format
        const transformedResults = prospects.map((prospect) => ({
          id: prospect._id,
          firstName: prospect.firstName,
          lastName: prospect.lastName,
          position: prospect.position,
          highSchool: prospect.highSchool || null,
          city: prospect.address?.city || null,
          state: prospect.address?.state || null,
          graduationYear: prospect.graduationYear || null,
          stars: prospect.rating || 0,
          height: prospect.height || null,
          weight: prospect.weight || null,
          gpa: prospect.academics?.gpa || null,
          fortyTime: prospect.speedData?.fortyYard || null,
          verticalJump: prospect.verticalJump || null,
          benchPress: prospect.benchPress || null,
          profilePicture: prospect.profilePicture || null,
          favorite: false, // Would need to fetch from user preferences
          recruitingStatus: prospect.recruitingStatus || "prospect",
          lastContact:
            prospect.lastContact || prospect.updatedAt?.split("T")[0] || null,
        }));

        setResults(transformedResults);
        setTotalResults(transformedResults.length);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setIsLoading(false);
      // Fallback to mock data if API fails
      const mockData = generateMockAthletes(100);
      setResults(mockData);
      setTotalResults(mockData.length);
    }

    // Update URL
    updateURL();
  }, [updateURL]);

  // Filter and sort results
  useEffect(() => {
    if (results.length === 0) return;

    console.log("Current filters:", filters);
    console.log("Results before filtering:", results.length);

    let filtered = [...results];

    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (athlete) =>
          (athlete.firstName &&
            athlete.firstName.toLowerCase().includes(query)) ||
          (athlete.lastName &&
            athlete.lastName.toLowerCase().includes(query)) ||
          (athlete.highSchool &&
            athlete.highSchool.toLowerCase().includes(query)) ||
          (athlete.city && athlete.city.toLowerCase().includes(query)) ||
          (athlete.state && athlete.state.toLowerCase().includes(query))
      );
    }

    // Apply physical filters
    // Height filter - convert string height to inches for comparison
    if (
      filters.physical.heightMin !== 60 ||
      filters.physical.heightMax !== 84
    ) {
      console.log(
        `Applying height filter: ${filters.physical.heightMin} - ${filters.physical.heightMax} inches`
      );
      const beforeCount = filtered.length;
      filtered = filtered.filter((athlete) => {
        if (!athlete.height) return false;
        // Convert height string to inches (e.g., "170" -> 170, "67" -> 67)
        const heightInInches = parseInt(athlete.height);
        if (isNaN(heightInInches)) return false;

        const included =
          heightInInches >= filters.physical.heightMin &&
          heightInInches <= filters.physical.heightMax;

        if (!included) {
          console.log(
            `Filtered out ${athlete.firstName} ${athlete.lastName}: height ${athlete.height} (${heightInInches}") not in range ${filters.physical.heightMin}-${filters.physical.heightMax}`
          );
        }

        return included;
      });
      console.log(
        `Height filter: ${beforeCount} -> ${filtered.length} athletes`
      );
    }

    // Weight filter
    if (
      filters.physical.weightMin !== 100 ||
      filters.physical.weightMax !== 350
    ) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.weight) return false;
        return (
          athlete.weight >= filters.physical.weightMin &&
          athlete.weight <= filters.physical.weightMax
        );
      });
    }

    // Vertical jump filter
    if (
      filters.physical.verticalJumpMin !== 0 ||
      filters.physical.verticalJumpMax !== 50
    ) {
      console.log(
        `Applying vertical jump filter: ${filters.physical.verticalJumpMin} - ${filters.physical.verticalJumpMax} inches`
      );
      const beforeCount = filtered.length;
      filtered = filtered.filter((athlete) => {
        if (
          !athlete.verticalJump ||
          athlete.verticalJump === null ||
          athlete.verticalJump === undefined
        )
          return false;

        // Convert to number if it's a string
        const verticalJumpValue =
          typeof athlete.verticalJump === "string"
            ? parseFloat(athlete.verticalJump)
            : athlete.verticalJump;

        if (isNaN(verticalJumpValue)) return false;

        const included =
          verticalJumpValue >= filters.physical.verticalJumpMin &&
          verticalJumpValue <= filters.physical.verticalJumpMax;

        if (!included) {
          console.log(
            `Filtered out ${athlete.firstName} ${athlete.lastName}: vertical jump ${athlete.verticalJump} (${verticalJumpValue}") not in range ${filters.physical.verticalJumpMin}-${filters.physical.verticalJumpMax}`
          );
        }

        return included;
      });
      console.log(
        `Vertical jump filter: ${beforeCount} -> ${filtered.length} athletes`
      );
    }

    // Apply academic filters
    if (filters.academic.gpaMin !== 2.0 || filters.academic.gpaMax !== 4.0) {
      console.log(
        `Applying GPA filter: ${filters.academic.gpaMin} - ${filters.academic.gpaMax}`
      );
      const beforeCount = filtered.length;
      filtered = filtered.filter((athlete) => {
        if (!athlete.gpa || athlete.gpa === null || athlete.gpa === undefined)
          return false;

        // Convert to number if it's a string
        const gpaValue =
          typeof athlete.gpa === "string"
            ? parseFloat(athlete.gpa)
            : athlete.gpa;

        if (isNaN(gpaValue)) return false;

        const included =
          gpaValue >= filters.academic.gpaMin &&
          gpaValue <= filters.academic.gpaMax;

        if (!included) {
          console.log(
            `Filtered out ${athlete.firstName} ${athlete.lastName}: GPA ${athlete.gpa} (${gpaValue}) not in range ${filters.academic.gpaMin}-${filters.academic.gpaMax}`
          );
        }

        return included;
      });
      console.log(`GPA filter: ${beforeCount} -> ${filtered.length} athletes`);
    }

    // Apply track filters
    if (filters.track.fortyMin !== 4.0 || filters.track.fortyMax !== 6.0) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.fortyTime) return false;
        return (
          athlete.fortyTime >= filters.track.fortyMin &&
          athlete.fortyTime <= filters.track.fortyMax
        );
      });
    }

    // Apply strength filters
    if (
      filters.strength.benchPressMin !== 0 ||
      filters.strength.benchPressMax !== 500
    ) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.benchPress) return false;
        return (
          athlete.benchPress >= filters.strength.benchPressMin &&
          athlete.benchPress <= filters.strength.benchPressMax
        );
      });
    }

    // Apply sport specific filters
    if (filters.sportSpecific.position) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.position) return false;
        return (
          athlete.position.toLowerCase() ===
          filters.sportSpecific.position.toLowerCase()
        );
      });
    }

    // Apply recruiting status filter
    if (filters.sportSpecific.recruitingStatus) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.recruitingStatus) return false;
        return (
          athlete.recruitingStatus.toLowerCase() ===
          filters.sportSpecific.recruitingStatus.toLowerCase()
        );
      });
    }

    // Apply location filters
    if (filters.location.state) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.state) return false;
        return (
          athlete.state.toLowerCase() === filters.location.state.toLowerCase()
        );
      });
    }

    if (filters.location.city) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.city) return false;
        return (
          athlete.city.toLowerCase() === filters.location.city.toLowerCase()
        );
      });
    }

    // Apply graduation year filter if specified
    if (filters.graduationYear && filters.graduationYear !== null) {
      filtered = filtered.filter((athlete) => {
        if (!athlete.graduationYear) return false;
        return athlete.graduationYear === filters.graduationYear;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy) {
        case "name":
          valueA = `${a.lastName}, ${a.firstName}`;
          valueB = `${b.lastName}, ${b.firstName}`;
          break;
        case "stars":
          valueA = a.stars;
          valueB = b.stars;
          break;
        case "position":
          valueA = a.position;
          valueB = b.position;
          break;
        case "height":
          valueA = parseInt(a.height) || 0;
          valueB = parseInt(b.height) || 0;
          break;
        case "weight":
          valueA = a.weight || 0;
          valueB = b.weight || 0;
          break;
        case "gpa":
          valueA = a.gpa || 0;
          valueB = b.gpa || 0;
          break;

        default:
          valueA = `${a.lastName}, ${a.firstName}`;
          valueB = `${b.lastName}, ${b.firstName}`;
      }

      // Apply sort order
      if (sortOrder === "asc") {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });

    // Apply pagination
    const startIndex = (page - 1) * rowsPerPage;
    const paginatedResults = filtered.slice(
      startIndex,
      startIndex + rowsPerPage
    );

    console.log("Final filtered results:", filtered.length);
    console.log("Paginated results:", paginatedResults.length);

    setFilteredResults(paginatedResults);
    setTotalResults(filtered.length);
  }, [results, searchQuery, sortBy, sortOrder, page, rowsPerPage, filters]);

  // Update active filters
  useEffect(() => {
    const newActiveFilters = [];

    // Physical filters
    if (
      filters.physical.heightMin !== 60 ||
      filters.physical.heightMax !== 84
    ) {
      newActiveFilters.push({
        id: "height",
        label: `Height: ${Math.floor(filters.physical.heightMin / 12)}'${
          filters.physical.heightMin % 12
        }" - ${Math.floor(filters.physical.heightMax / 12)}'${
          filters.physical.heightMax % 12
        }"`,
        category: "physical",
        onDelete: () =>
          handleFilterReset("physical", { heightMin: 60, heightMax: 84 }),
      });
    }

    if (
      filters.physical.weightMin !== 100 ||
      filters.physical.weightMax !== 350
    ) {
      newActiveFilters.push({
        id: "weight",
        label: `Weight: ${filters.physical.weightMin} - ${filters.physical.weightMax} lbs`,
        category: "physical",
        onDelete: () =>
          handleFilterReset("physical", { weightMin: 100, weightMax: 350 }),
      });
    }

    if (
      filters.physical.verticalJumpMin !== 0 ||
      filters.physical.verticalJumpMax !== 50
    ) {
      newActiveFilters.push({
        id: "verticalJump",
        label: `Vertical Jump: ${filters.physical.verticalJumpMin}" - ${filters.physical.verticalJumpMax}"`,
        category: "physical",
        onDelete: () =>
          handleFilterReset("physical", {
            verticalJumpMin: 0,
            verticalJumpMax: 50,
          }),
      });
    }

    // Academic filters
    if (filters.academic.gpaMin !== 2.0 || filters.academic.gpaMax !== 4.0) {
      newActiveFilters.push({
        id: "gpa",
        label: `GPA: ${filters.academic.gpaMin.toFixed(
          1
        )} - ${filters.academic.gpaMax.toFixed(1)}`,
        category: "academic",
        onDelete: () =>
          handleFilterReset("academic", { gpaMin: 2.0, gpaMax: 4.0 }),
      });
    }

    if (filters.academic.satMin !== 400 || filters.academic.satMax !== 1600) {
      newActiveFilters.push({
        id: "sat",
        label: `SAT: ${filters.academic.satMin} - ${filters.academic.satMax}`,
        category: "academic",
        onDelete: () =>
          handleFilterReset("academic", { satMin: 400, satMax: 1600 }),
      });
    }

    // Track filters
    if (filters.track.fortyMin !== 4.0 || filters.track.fortyMax !== 6.0) {
      newActiveFilters.push({
        id: "forty",
        label: `40m: ${filters.track.fortyMin.toFixed(
          1
        )} - ${filters.track.fortyMax.toFixed(1)} sec`,
        category: "track",
        onDelete: () =>
          handleFilterReset("track", { fortyMin: 4.0, fortyMax: 6.0 }),
      });
    }

    // Strength filters
    if (
      filters.strength.benchPressMin !== 0 ||
      filters.strength.benchPressMax !== 500
    ) {
      newActiveFilters.push({
        id: "benchPress",
        label: `Bench Press: ${filters.strength.benchPressMin} - ${filters.strength.benchPressMax} lbs`,
        category: "strength",
        onDelete: () =>
          handleFilterReset("strength", {
            benchPressMin: 0,
            benchPressMax: 500,
          }),
      });
    }

    // Sport specific filters
    if (filters.sportSpecific.position) {
      newActiveFilters.push({
        id: "position",
        label: `Position: ${filters.sportSpecific.position}`,
        category: "sportSpecific",
        onDelete: () => handleFilterReset("sportSpecific", { position: "" }),
      });
    }

    if (filters.sportSpecific.recruitingStatus) {
      newActiveFilters.push({
        id: "recruitingStatus",
        label: `Status: ${filters.sportSpecific.recruitingStatus}`,
        category: "sportSpecific",
        onDelete: () =>
          handleFilterReset("sportSpecific", { recruitingStatus: "" }),
      });
    }

    // Location filters
    if (filters.location.state) {
      newActiveFilters.push({
        id: "state",
        label: `State: ${filters.location.state}`,
        category: "location",
        onDelete: () => handleFilterReset("location", { state: "" }),
      });
    }

    if (filters.location.city) {
      newActiveFilters.push({
        id: "city",
        label: `City: ${filters.location.city}`,
        category: "location",
        onDelete: () => handleFilterReset("location", { city: "" }),
      });
    }

    // Graduation Year filter
    if (
      filters.graduationYear &&
      filters.graduationYear !== null &&
      typeof filters.graduationYear !== "object"
    ) {
      newActiveFilters.push({
        id: "graduationYear",
        label: `Graduation Year: ${filters.graduationYear}`,
        category: "graduationYear",
        onDelete: () => handleFilterReset("graduationYear", null),
      });
    }

    setActiveFilters(newActiveFilters);
  }, [filters]);

  // Fetch results on initial load
  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem("athleteSearchViewMode", viewMode);
  }, [viewMode]);

  // Handle search query change
  const handleSearchQueryChange = (value) => {
    setSearchQuery(value);
    setPage(1);
    // Don't refetch - just apply search to existing data
    // debouncedFetchResults();
  };

  // Handle filter change
  const handleFilterChange = (category, values) => {
    console.log(`Filter change - category: ${category}, values:`, values);

    // Special handling for graduationYear since it's not an object
    if (category === "graduationYear") {
      setFilters((prev) => ({
        ...prev,
        graduationYear: values,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          ...values,
        },
      }));
    }

    setPage(1);
    // Don't refetch - just apply filters to existing data
    // debouncedFetchResults();
  };

  // Handle filter reset
  const handleFilterReset = (category, values) => {
    if (category === "graduationYear") {
      setFilters((prev) => ({
        ...prev,
        graduationYear: null,
      }));
    } else {
      handleFilterChange(category, values);
    }
    setPage(1);
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setFilters({
      physical: {
        heightMin: 60,
        heightMax: 84,
        weightMin: 100,
        weightMax: 350,
        verticalJumpMin: 0,
        verticalJumpMax: 50,
      },
      academic: {
        gpaMin: 2.0,
        gpaMax: 4.0,
        satMin: 400,
        satMax: 1600,
      },
      track: {
        fortyMin: 4.0,
        fortyMax: 6.0,
        hundredMin: 9.5,
        hundredMax: 14.0,
        twoHundredMin: 19.0,
        twoHundredMax: 28.0,
        fourHundredMin: 43.0,
        fourHundredMax: 70.0,
      },
      strength: {
        benchPressMin: 0,
        benchPressMax: 500,
        benchRepsMin: 0,
        benchRepsMax: 50,
        squatMin: 0,
        squatMax: 700,
      },
      sportSpecific: {
        sport: "football",
        position: "",
        recruitingStatus: "",
        stats: {},
      },
      location: {
        state: "",
        city: "",
        radius: 50,
      },
      graduationYear: null,
    });
    setSearchQuery("");
    setPage(1);
    // Don't refetch - just clear filters and apply to existing data
    // fetchResults();
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
    updateURL();
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
    updateURL();
  };

  // Handle sort change
  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    updateURL();
  };

  // Handle filter panel toggle
  const handleFilterPanelToggle = () => {
    setFilterPanelOpen(!filterPanelOpen);
  };

  // Handle saved searches panel toggle
  const handleSavedSearchesPanelToggle = () => {
    setSavedSearchesPanelOpen(!savedSearchesPanelOpen);
  };

  // Handle apply saved search
  const handleApplySavedSearch = (savedSearch) => {
    setFilters(savedSearch.filters);
    setSearchQuery(savedSearch.query || "");
    setSortBy(savedSearch.sortBy || "name");
    setSortOrder(savedSearch.sortOrder || "asc");

    setPage(1);
    // Don't refetch - just apply saved search to existing data
    // fetchResults();
    setSavedSearchesPanelOpen(false);
  };

  // Handle view profile
  const handleViewProfile = (athlete) => {
    navigate(`/prospects/profile/${athlete.id}`);
  };

  // Handle add to board
  const handleAddToBoard = (athlete) => {
    // TODO: Implement add to board functionality
    console.log("Add to board:", athlete);
  };

  // Handle toggle favorite
  const handleToggleFavorite = (athlete) => {
    // TODO: Implement favorite toggle functionality
    console.log("Toggle favorite:", athlete);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Search Header */}
      <SearchHeader
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onFilterToggle={handleFilterPanelToggle}
        onSavedSearchesToggle={handleSavedSearchesPanelToggle}
        filterPanelOpen={filterPanelOpen}
        savedSearchesPanelOpen={savedSearchesPanelOpen}
      />

      <Box sx={{ display: "flex", mt: 2 }}>
        {/* Filter Panel */}
        <AdvancedFilterPanel
          open={filterPanelOpen}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAllFilters={handleClearAllFilters}
          onClose={handleFilterPanelToggle}
        />

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            ml: { xs: 0, md: filterPanelOpen ? 2 : 0 },
            width: "100%",
          }}
        >
          {/* Filter Chips and Controls */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: { xs: 2, md: 0 },
                }}
              >
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  {isLoading ? "Searching..." : `${totalResults} results`}
                </Typography>
              </Box>

              <SortControls
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <FilterChips
              filters={activeFilters}
              onClearAll={handleClearAllFilters}
            />
          </Paper>

          {/* Search Results */}
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredResults.length > 0 ? (
            <>
              <SearchResultsGrid
                results={filteredResults}
                viewMode={viewMode}
                onViewProfile={handleViewProfile}
                onAddToBoard={handleAddToBoard}
                onToggleFavorite={handleToggleFavorite}
              />

              <SearchPagination
                count={totalResults}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          ) : (
            <NoResultsFound
              searchQuery={searchQuery}
              hasActiveFilters={activeFilters.length > 0}
              onClearFilters={handleClearAllFilters}
              isInitialState={
                !searchQuery &&
                activeFilters.length === 0 &&
                Object.keys(queryParams).length === 0
              }
            />
          )}
        </Box>

        {/* Saved Searches Panel - Hidden for now */}
        {false && (
          <SavedSearchesPanel
            open={savedSearchesPanelOpen}
            onClose={handleSavedSearchesPanelToggle}
            onApply={handleApplySavedSearch}
            currentFilters={filters}
            currentQuery={searchQuery}
            currentSortBy={sortBy}
            currentSortOrder={sortOrder}
          />
        )}
      </Box>
    </Container>
  );
};

export default AthleteSearchResults;
